import { Button } from "@gigpro/ui/Button";
import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import { type AccountLinking, LoginMethodEnum } from "@sidegigllc/api/client";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { markAccountLinkingSkipped } from "domains/auth/accountLinking";
import { startAccountLinking } from "domains/auth/initializeAuth";

const CONNECT_SENTENCE = "connect the two so you can login with either in the future\n";

const ctaLabelLookup: Record<LoginMethodEnum, string> = {
  [LoginMethodEnum.EMAIL]: "Login with Email Address",
  [LoginMethodEnum.PHONE]: "Login with Phone Number",
};

// Keyed on the candidate's method: it names the credential the caller did not just arrive by.
const headingCopy: Record<LoginMethodEnum, { named: (identifier: string) => string; withheld: string }> = {
  [LoginMethodEnum.EMAIL]: {
    named: (identifier) =>
      `We found your phone number associated with the email address ${identifier}.\n\nLogin with your email and ${CONNECT_SENTENCE}`,
    withheld: `We found your phone number associated with an email address on file.\n\nLogin with your email and ${CONNECT_SENTENCE}`,
  },
  [LoginMethodEnum.PHONE]: {
    named: (identifier) =>
      `We found your email address associated with the phone number ${identifier}.\n\nLogin with your phone number and ${CONNECT_SENTENCE}`,
    withheld: `We found your email address associated with a phone number on file.\n\nLogin with your phone number and ${CONNECT_SENTENCE}`,
  },
};

type AccountLinkingPromptProps = {
  accounts: Array<AccountLinking> | undefined;
  /** `h1` on the standalone page, `DialogTitle` in the modal — Radix requires its own title inside. */
  Heading: React.ComponentType<{ className?: string; children: React.ReactNode }>;
};

export function AccountLinkingPrompt({ accounts, Heading }: Readonly<AccountLinkingPromptProps>) {
  // Minting is rate-limited; a failure must leave the manager signed in on the prompt.
  const linking = useMutation({
    mutationFn: startAccountLinking,
    // `fromThrown` would flatten our own Error into a generic message; customFetch only throws ApiError.
    onError: (error) => toast.error(error instanceof ApiError ? error.getMainErrorMessage() : error.message),
  });

  const candidate = accounts?.[0];

  if (!candidate) {
    return (
      <>
        <Heading className="text-large font-semibold">We couldn't find an account to link to</Heading>
        <Button variant="outlined" className="w-full" asChild>
          <Link to="/signup" search={{ skipAccountLinking: true }} onClick={markAccountLinkingSkipped}>
            Return to signup
          </Link>
        </Button>
      </>
    );
  }

  const copy = headingCopy[candidate.login_method];
  let heading = "Looks like you previously logged in with another account";
  if (copy) heading = candidate.masked_identifier ? copy.named(candidate.masked_identifier) : copy.withheld;
  // `||`, not `??`: an unrecognised login method misses the lookup and must still label the button.
  const buttonLabel = ctaLabelLookup[candidate.login_method] || "Log in with your other account to link them";

  return (
    <>
      <Heading className="text-large font-semibold whitespace-pre-line">{heading}</Heading>
      <Button
        variant="contained"
        className="mb-4 w-full bg-success hover:bg-success-hover"
        onClick={() => linking.mutate()}
        loading={linking.isPending}
      >
        {buttonLabel}
      </Button>
      <Button variant="outlined" className="mb-1 w-full" asChild>
        <Link to="/signup" search={{ skipAccountLinking: true }} onClick={markAccountLinkingSkipped}>
          Continue to Create a New Account and Location
        </Link>
      </Button>
      <p className="text-small font-bold text-success">
        Changed your email address or phone number? Text support at 79425 or email us at{" "}
        <a href="mailto:support@qwick.com" className="hover:underline">
          support@qwick.com
        </a>{" "}
        for assistance updating your information
      </p>
    </>
  );
}
